import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Clock, Gem, RefreshCcw, Star, Trophy, Users, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  getAvailableMissions,
  getTodayMissions,
  getWeekMissions,
  getAllMissions,
  completeMission,
  getPlayerStats,
  type PlayerStats
} from '@/services/pointsService';
import { PLAYER_LEVELS, BADGES, type Mission } from '@/data/missions';
import { getFriendInvitations, getInvitationProgress, type FriendInvitation } from '@/services/invitationService';
import { InviteFriendsModal } from './InviteFriendsModal';
import { DailyQuizModal } from './DailyQuizModal';
import { Gems } from './Gems';

interface MissionsPanelProps {
  playerId: string;
}

interface PlayerMission {
  id: string;
  player_id: string;
  mission_id: string;
  completed_at: string;
  points_earned: number;
}

export function MissionsPanel({ playerId }: MissionsPanelProps) {
  const [missions, setMissions] = useState<Mission[]>([]);
  const [todayMissions, setTodayMissions] = useState<PlayerMission[]>([]);
  const [weekMissions, setWeekMissions] = useState<PlayerMission[]>([]);
  const [allMissions, setAllMissions] = useState<PlayerMission[]>([]);
  const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Invitation state
  const [invitations, setInvitations] = useState<FriendInvitation[]>([]);
  const [invitationProgress, setInvitationProgress] = useState({ validated: 0, total: 0, remaining: 5 });

  const [showInviteModal, setShowInviteModal] = useState(false);

  // Quiz state
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedQuizMissionId, setSelectedQuizMissionId] = useState<string | null>(null);

  // Reset timer state
  const [timeLeft, setTimeLeft] = useState<string>('');

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const nextReset = new Date();
      nextReset.setUTCHours(24, 0, 0, 0); // Next midnight UTC

      const diff = nextReset.getTime() - now.getTime();

      if (diff <= 0) return '00:00:00';

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      return `${hours}h ${String(minutes).padStart(2, '0')}m`;
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 10000);

    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadData();
  }, [playerId]);

  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const [missionsData, todayData, weekData, allMissionsData, statsData, invitationsData, progressData] = await Promise.all([
        getAvailableMissions(),
        getTodayMissions(playerId),
        getWeekMissions(playerId),
        getAllMissions(playerId),
        getPlayerStats(playerId),
        getFriendInvitations(playerId),
        getInvitationProgress(playerId)
      ]);

      setMissions(missionsData);
      setTodayMissions(todayData);
      setWeekMissions(weekData);
      setAllMissions(allMissionsData);
      setPlayerStats(statsData);
      setInvitations(invitationsData);
      setInvitationProgress(progressData);
    } catch (error) {
      console.error('Error loading missions data:', error);
      toast.error('Erreur lors du chargement des missions');
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  const silentLoadData = useCallback(() => loadData(false), [loadData]);

  const getMissionRecord = (missionId: string, type: string): PlayerMission | undefined => {
    if (type === 'daily') {
      return todayMissions.find(m => m.mission_id === missionId);
    }
    if (type === 'weekly') {
      return weekMissions.find(m => m.mission_id === missionId);
    }
    if (type === 'one_time') {
      return allMissions.find(m => m.mission_id === missionId);
    }
    return undefined;
  };

  const getDailyMissions = () => missions.filter(m => m.type === 'daily');
  const getWeeklyMissions = () => missions.filter(m => m.type === 'weekly');
  const getOneTimeMissions = () => missions.filter(m => m.type === 'one_time');

  const isBoostActive = playerStats?.boost_end_date && new Date(playerStats.boost_end_date) > new Date();

  const getDisplayPoints = (mission: Mission) => {
    const basePoints = mission.points_reward;
    if (isBoostActive && (mission.type === 'daily' || mission.type === 'weekly')) {
      return basePoints * 1;
    }
    return basePoints;
  };

  return (
    <>
      {loading && missions.length === 0 ? (
        <Card className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="space-y-2">
              <div className="h-3 bg-muted rounded"></div>
              <div className="h-3 bg-muted rounded w-3/4"></div>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">

          {/* Daily missions */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-semibold">Daily Missions</h3>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                <RefreshCcw className="w-3 h-3" />
                <span className="font-medium gradient-text">{timeLeft}</span>
              </div>
            </div>
            <div className="space-y-3">
              {getDailyMissions().map((mission) => {
                const record = getMissionRecord(mission.id, mission.type);
                const completed = !!record;
                const isFailed = record && Number(record.points_earned) === 0;

                return (
                  <div key={mission.id} className="flex items-center justify-between p-3 border px-perso-20 rounded-custom-paddings">
                    <div className="flex items-center gap-3">
                      {completed ? (
                        isFailed ? (
                          <XCircle className="w-5 h-5 text-red-500" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )
                      ) : (
                        <div className="w-5 h-5 border-2 border-muted-foreground rounded-full" />
                      )}
                      <div>
                        <p className={`font-medium ${isFailed ? 'text-muted-foreground line-through' : ''}`}>
                          {mission.description}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                            <span className="flex items-center gap-0.5">+{getDisplayPoints(mission)} <Gems size={12} /></span>


                            {isBoostActive && (
                              <Badge variant="outline" className="text-[10px] h-4 border-yellow-500/50 text-yellow-500 py-0 px-1 font-bold animate-pulse">
                                X2
                              </Badge>
                            )}
                            {mission.xp_reward && (
                              <span className="flex items-center gap-0.5 ml-1 px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded-full text-[10px] font-bold">
                                +{mission.xp_reward} XP
                              </span>
                            )}
                          </p>
                        </div>
                        {mission.name === 'daily_quiz' && !completed && (
                          <Button
                            size="sm"
                            className="mt-2 h-7"
                            onClick={() => {
                              setSelectedQuizMissionId(mission.id);
                              setShowQuizModal(true);
                            }}
                          >
                            👉 Play Daily Quiz
                          </Button>
                        )}
                      </div>
                    </div>

                    <Badge variant={completed ? (isFailed ? "destructive" : "default") : "outline"}>
                      {completed ? (isFailed ? 'Failed' : 'Completed') : 'In Progress'}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Weekly missions */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <h3 className="text-lg font-semibold">Weekly Missions</h3>
            </div>
            <div className="space-y-3">
              {getWeeklyMissions().map((mission) => {
                const record = getMissionRecord(mission.id, mission.type);
                const completed = !!record;
                const isFailed = record && Number(record.points_earned) === 0;

                return (
                  <div key={mission.id}>
                    <div className="flex items-center justify-between p-3 border px-perso-20 rounded-custom-paddings">
                      <div className="flex items-center gap-3">
                        {completed ? (
                          isFailed ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )
                        ) : (
                          <div className="w-5 h-5 border-2 border-muted-foreground rounded-full" />
                        )}
                        <div className="flex-1">
                          <p className={`font-medium ${isFailed ? 'text-muted-foreground line-through' : ''}`}>
                            {mission.description}
                          </p>
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                              <span className="flex items-center gap-0.5">+{getDisplayPoints(mission)} <Gems size={12} /></span>
                              {isBoostActive && (
                                <Badge variant="outline" className="text-[10px] h-4 border-yellow-500/50 text-yellow-500 py-0 px-1 font-bold animate-pulse">
                                  X2
                                </Badge>
                              )}
                              {mission.xp_reward && (
                                <span className="flex items-center gap-0.5 ml-1 px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded-full text-[10px] font-bold">
                                  +{mission.xp_reward} XP
                                </span>
                              )}
                            </p>
                          </div>

                        </div>
                      </div>
                      <Badge variant={completed ? (isFailed ? "destructive" : "default") : "outline"}>
                        {completed ? (isFailed ? 'Failed' : 'Completed') : 'In Progress'}
                      </Badge>
                    </div>
                    {mission.name === 'weekly_playtime' && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        Spend quality time with a game you purchased in the past 7 days to complete this mission.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* One-time missions */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-purple-500" />
              <h3 className="text-lg font-semibold">One-time Missions</h3>
            </div>
            <div className="space-y-3">
              {getOneTimeMissions().map((mission) => {
                const record = getMissionRecord(mission.id, mission.type);
                const completed = !!record;
                const isFailed = record && Number(record.points_earned) === 0;
                const isInviteMission = mission.name === 'invite_friend';

                return (
                  <div key={mission.id} className="flex items-center justify-between p-3 border px-perso-20 rounded-custom-paddings">
                    <div className="flex items-center gap-3 flex-1">
                      {completed ? (
                        isFailed ? (
                          <XCircle className="w-5 h-5 text-red-500" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        )
                      ) : (
                        <div className="w-5 h-5 border-2 border-muted-foreground rounded-full" />
                      )}
                      <div className="flex-1">
                        <p className={`font-medium ${isFailed ? 'text-muted-foreground line-through' : ''}`}>
                          {mission.description}
                        </p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                          {isInviteMission ? <>+20 <Gems size={12} /> and <span className="flex items-center gap-0.5 ml-1 px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded-full text-[10px] font-bold">+{mission.xp_reward} XP</span> per invitation</> : <><span className="flex items-center gap-0.5">+{getDisplayPoints(mission)} <Gems size={12} /></span></>}
                        </p>

                        {isInviteMission && (
                          <div className="mt-2 max-w-xs">
                            <div className="flex justify-between text-xs mb-1">
                              <span>Progress</span>
                              <span>{invitationProgress.validated}/5</span>
                            </div>
                            <Progress value={(invitationProgress.validated / 5) * 100} className="h-1.5" />
                          </div>
                        )}
                      </div>
                    </div>

                    {isInviteMission ? (
                      <Button
                        onClick={() => setShowInviteModal(true)}
                        variant="outline"
                        size="sm"
                        className="ml-3"
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Invite
                      </Button>
                    ) : (
                      <Badge variant={completed ? (isFailed ? "destructive" : "default") : "outline"}>
                        {completed ? (isFailed ? 'Failed' : 'Completed') : 'In Progress'}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* Invite Friends Modal */}
      <InviteFriendsModal
        open={showInviteModal}
        onOpenChange={setShowInviteModal}
        playerId={playerId}
        invitations={invitations}
        remaining={invitationProgress.remaining}
        onInvitationSent={silentLoadData}
      />

      {/* Daily Quiz Modal */}
      {selectedQuizMissionId && (
        <DailyQuizModal
          isOpen={showQuizModal}
          onClose={() => setShowQuizModal(false)}
          onComplete={silentLoadData}
          missionId={selectedQuizMissionId}
          playerId={playerId}
        />
      )}
    </>
  );
}
